import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  ListItemIcon,
  ListItemText,
  TableSortLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BoomIcon from '@mui/icons-material/Dehaze';
import SprayProfilesIcon from '@mui/icons-material/SettingsEthernet';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ShowerIcon from '@mui/icons-material/Shower';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import AlertIcon from '@mui/icons-material/AddAlert';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import authService from '../../api-authorization/AuthorizeService';
import RequestOverlay from '../../components/RequestOverlay';
import styles from './AdminConfigTractors.module.css';
import AddTractorDialog from './AddTractorDialog/AddTractorDialog';
import UpdateTractorDialog from './UpdateTractorDialog.js/UpdateTractorDialog';
import UpdateDeviceDialog from './UpdateDeviceDialog.js/UpdateDeviceDialog';
import DuplicateTractorConfigDialog from './DuplicateTractorConfigDialog/DuplicateTractorConfigDialog';

const AdminConfigTractors = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  const [isAddTractorDialogOpen, setIsAddTractorDialogOpen] = useState(false);
  const [isUpdateTractorDialogOpen, setIsUpdateTractorDialogOpen] = useState(false);
  const [isUpdateDeviceDialogOpen, setIsUpdateDeviceDialogOpen] = useState(false);
  const [isDuplicateTractorConfigDialogOpen, setIsDuplicateTractorConfigDialogOpen] = useState(false);

  const [tractorToUpdate, setTractorToUpdate] = useState(null);
  const [deviceToUpdate, setDeviceToUpdate] = useState(null);
  const [tractorDevices, setTractorDevices] = useState({});
  const [tractors, setTractors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [unaffiliatedDevices, setUnaffiliatedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);

  // Menu anchor state for actions dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTractor, setSelectedTractor] = useState(null);

  const handleMenuOpen = (event, tractor) => {
    setAnchorEl(event.currentTarget);
    setSelectedTractor(tractor);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTractor(null);
  };

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  const selectedClient = {
    id: 1
  };

  const fetchTractorDevices = async (tractor) => {
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/devices/all-by-tractor?tractorId=${tractor.id}`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const deviceData = await response.json();
        setTractorDevices((prev) => ({ ...prev, [tractor.id]: deviceData }));
      } else {
        toast.error(`${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setTimeout(() => {
        toast.error('An unexpected error occured! Please try again later!');
      }, 1000);
    }
  };

  const fetchTractors = async () => {
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/all`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const tractorData = await response.json();
      setTractors(tractorData);
      setLoading(false);
      return tractorData;
    } catch (error) {
      toast.error('Communication with server failed. Please reload your page.');
      return [];
    }
  };

  const fetchUnaffiliatedDevices = async () => {
    try {
      const token = await authService.getAccessToken();

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/devices/unaffiliated`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      const unaffiliatedDevices = await response.json();
      setUnaffiliatedDevices(unaffiliatedDevices);
    } catch (error) {
      toast.error('Communication with server failed. Please reload your page.');
    }
  };

  // Fetch all devices and store in state
  const fetchAllDevices = async () => {
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/devices/all`,
        {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const deviceData = await response.json();
        setDevices(deviceData);
      } else {
        toast.error(`${response.status} ${response.statusText}`);
      }
    } catch (error) {
      toast.error('Failed to fetch devices.');
    }
  };

  useEffect(() => {
    fetchUnaffiliatedDevices();
    fetchTractors();
    fetchAllDevices();
  }, []);

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const openAddTractorDialog = () => {
    setIsAddTractorDialogOpen(true);
  };
  const closeAddTractorDialog = () => {
    setIsAddTractorDialogOpen(false);
  };

  const handleAddTractorSuccess = async () => {
    await fetchTractors();
    await fetchAllDevices();
    closeAddTractorDialog();
  };

  const openUpdateTractorDialog = (tractor) => {
    setTractorToUpdate(tractor);
    setDeviceToUpdate(getDeviceForTractor(tractor.id)); // Pass the device
    setIsUpdateTractorDialogOpen(true);
    handleMenuClose();
  };

  const closeUpdateTractorDialog = () => {
    setTractorToUpdate(null);
    setIsUpdateTractorDialogOpen(false);
  };

  const handleUpdateTractorSuccess = () => {
    fetchTractors();
    fetchAllDevices(); 
    closeUpdateTractorDialog();
  };

  const closeUpdateDeviceDialog = () => {
    setTractorToUpdate(null);
    setDeviceToUpdate(null);
    setIsUpdateDeviceDialogOpen(false);
  };

  const handleUpdateDeviceSuccess = (tractor) => {
    closeUpdateDeviceDialog();
    fetchTractorDevices(tractor);
  };

  const openDuplicateTractorConfigDialog = () => {
    setTractorToUpdate(selectedTractor);
    setIsDuplicateTractorConfigDialogOpen(true);
    handleMenuClose();
  };

  const closeDuplicateTractorConfigDialog = () => {
    setIsDuplicateTractorConfigDialogOpen(false);
  };

  const handleDuplicateTractorConfigSuccess = () => {
    fetchTractors();
    closeDuplicateTractorConfigDialog();
  };

  const handleNavigateToDeviceCommands = () => {
    const url = `/admin/sprayers/device-commands?tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToDeviceDiagnostics = () => {
    const url = `/admin/sprayers/device-diagnostic?tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToSprayerHistory = () => {
    const url = `/admin/sprayers/history?tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToAlerts = () => {
    const url = `/admin/sprayers/alerts?tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToFlowCalibration = () => {
    const url = `/admin/sprayers/flow-calibration?clientId=${selectedTractor.clientId}&tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToNozzleProfiles = () => {
    const url = `/admin/sprayers/nozzleprofiles?clientId=${selectedTractor.clientId}&tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToBooms = () => {
    const url = `/admin/sprayers/booms?clientId=${selectedTractor.clientId}&tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const handleNavigateToNozzles = () => {
    const url = `/admin/sprayers/nozzles?clientId=${selectedTractor.clientId}&tractorId=${selectedTractor.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleMenuClose();
  };

  const sortedTractors = [...tractors].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Helper to get device for a tractor
  const getDeviceForTractor = (tractorId) =>
    devices.find((d) => d.tractorId === tractorId);

  return (
    <>
      <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
        <RequestOverlay
          isLoading={loading}
          id="loading-overlay"
          errorTitle="Your data couldn't be loaded."
          errorSubtitle="Please contact an administrator."
        />
        <Typography variant="h4" sx={{ pt: { xs: 2, sm: 0 }, pb: 1 }}>
          Sprayers
        </Typography>
        {(
          <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
            <TextField
              placeholder="Filter"
              variant="outlined"
              onChange={handleFilterChange}
              value={filter}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterAltIcon />
                    </InputAdornment>
                  )
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddTractorDialog}
            >
              Add Sprayer
            </Button>
          </Stack>
        )}
        <TableContainer component={Paper} sx={{ my: 1 }}>
          <Table>
            <TableHead sx={{ '& .MuiTableCell-root': { py: { xs: 0.5, sm: 2 } } }}>
              <TableRow>
                <TableCell style={{ textAlign: 'center' }}>
                  <span>
                    Actions
                  </span>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Sprayer<br />Name
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'deviceSerialNumber'}
                    direction={sortBy === 'deviceSerialNumber' ? sortOrder : 'asc'}
                    onClick={() => handleSort('deviceSerialNumber')}
                  >
                    Device<br />Serial Number
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'defaultTargetApplicationRateGalAcre'}
                    direction={sortBy === 'defaultTargetApplicationRateGalAcre' ? sortOrder : 'asc'}
                    onClick={() => handleSort('defaultTargetApplicationRateGalAcre')}
                  >
                    Default Target<br />App Rate (gal/ac)
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'targetPressurePsi'}
                    direction={sortBy === 'targetPressurePsi' ? sortOrder : 'asc'}
                    onClick={() => handleSort('targetPressurePsi')}
                  >
                    Target<br />Pressure (psi)
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'nozzleProfileCount'}
                    direction={sortBy === 'nozzleProfileCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('nozzleProfileCount')}
                  >
                    Nozzle Profile<br />Count
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'boomCount'}
                    direction={sortBy === 'boomCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('boomCount')}
                  >
                    Boom<br />Count
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'nozzleCount'}
                    direction={sortBy === 'nozzleCount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('nozzleCount')}
                  >
                    Nozzle<br />Count
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'flowPulsesPerGal1'}
                    direction={sortBy === 'flowPulsesPerGal1' ? sortOrder : 'asc'}
                    onClick={() => handleSort('flowPulsesPerGal1')}
                  >
                    Flow 1<br />(pul/gal)
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'flowPulsesPerGal2'}
                    direction={sortBy === 'flowPulsesPerGal2' ? sortOrder : 'asc'}
                    onClick={() => handleSort('flowPulsesPerGal2')}
                  >
                    Flow 2<br />(pul/gal)
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortBy === 'flowPulsesPerGal3'}
                    direction={sortBy === 'flowPulsesPerGal3' ? sortOrder : 'asc'}
                    onClick={() => handleSort('flowPulsesPerGal3')}
                  >
                    Flow 3<br />(pul/gal)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTractors
                .filter((tractor) => {
                  const filterLower = filter.toLowerCase();
                  const name = (tractor.name || '').toLowerCase();
                  const serialNumber = (tractor.deviceSerialNumber || '').toLowerCase();
                  return name.includes(filterLower) || serialNumber.includes(filterLower);
                })
                .map((tractor) => {
                  const device = getDeviceForTractor(tractor.id);
                  return (
                    <React.Fragment key={tractor.id}>
                      <TableRow>
                        <TableCell style={{ textAlign: 'center' }}>
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, tractor)}
                            aria-label="more actions"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{tractor.name}</TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: device?.serialNumber ? 'inherit' : 'red' }}>
                            {device?.serialNumber
                              ? device?.externalSerialNumber
                                ? `${device.externalSerialNumber} - ${device.serialNumber}`
                                : device.serialNumber
                              : '---'}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: tractor.defaultTargetApplicationRateGalAcre === 0 ? 'red' : 'inherit' }}>
                            {tractor.defaultTargetApplicationRateGalAcre}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: tractor.targetPressurePsi === 0 ? 'red' : 'inherit' }}>
                            {tractor.targetPressurePsi}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: tractor.nozzleProfileCount === 0 ? 'red' : 'inherit' }}>
                            {tractor.nozzleProfileCount}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: tractor.boomCount === 0 ? 'red' : 'inherit' }}>
                            {tractor.boomCount}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: tractor.nozzleCount === 0 ? 'red' : 'inherit' }}>
                            {tractor.nozzleCount}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: device?.flowPulsesPerGal1 ? 'inherit' : 'red' }}>
                            {device?.flowPulsesPerGal1 ?? '---'}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: device?.flowPulsesPerGal2 ? 'inherit' : 'red' }}>
                            {device?.flowPulsesPerGal2 ?? '---'}
                          </span>
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          <span style={{ color: device?.flowPulsesPerGal3 ? 'inherit' : 'red' }}>
                            {device?.flowPulsesPerGal3 ?? '---'}
                          </span>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => openUpdateTractorDialog(selectedTractor)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Sprayer</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToBooms}>
            <ListItemIcon>
              <BoomIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Booms</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToNozzles}>
            <ListItemIcon>
              <ShowerIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Nozzles</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToNozzleProfiles}>
            <ListItemIcon>
              <SprayProfilesIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Nozzle Profiles</ListItemText>
          </MenuItem>
          <MenuItem onClick={openDuplicateTractorConfigDialog}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate Configuration</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToFlowCalibration}>
            <ListItemIcon>
              <SpeedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Flow Meter Calibration</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToDeviceCommands}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Device Commands</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToDeviceDiagnostics}>
            <ListItemIcon>
              <DevicesIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Device Diagnostics</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToAlerts}>
            <ListItemIcon>
              <AlertIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Real Time Alerts</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleNavigateToSprayerHistory}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change History</ListItemText>
          </MenuItem>
        </Menu>

        {/* if-gates force to re-render with the tractor to be updated */}
        {isAddTractorDialogOpen && (
          <AddTractorDialog
            client={selectedClient}
            unaffiliatedDevices={unaffiliatedDevices}
            open={isAddTractorDialogOpen}
            onClose={closeAddTractorDialog}
            onSuccess={handleAddTractorSuccess}
          />
        )}
        {isUpdateTractorDialogOpen && (
          <UpdateTractorDialog
            tractor={tractorToUpdate}
            device={deviceToUpdate}
            unaffiliatedDevices={unaffiliatedDevices}
            open={isUpdateTractorDialogOpen}
            onClose={closeUpdateTractorDialog}
            onSuccess={handleUpdateTractorSuccess}
          />
        )}
        {isUpdateDeviceDialogOpen && (
          <UpdateDeviceDialog
            open={isUpdateDeviceDialogOpen}
            client={selectedClient}
            tractor={tractorToUpdate}
            device={deviceToUpdate}
            onClose={closeUpdateDeviceDialog}
            onSuccess={() => handleUpdateDeviceSuccess(tractorToUpdate)}
          />
        )}
        {isDuplicateTractorConfigDialogOpen && (
          <DuplicateTractorConfigDialog
            tractor={tractorToUpdate}
            open={isDuplicateTractorConfigDialogOpen}
            onClose={closeDuplicateTractorConfigDialog}
            onSuccess={handleDuplicateTractorConfigSuccess}
          />
        )}
      </Box>
    </>
  );
};

export default AdminConfigTractors;